import { JobTask, JobTaskData } from '@root/globalTypes';
import { isBefore, isToday, startOfToday } from 'date-fns';
import { getAction, isSchedulable, isWithoutSchedule, sortByProp } from '../helpers';
import { parseJobDays } from '../days/helpers';
import { JobDay } from '../days/types';
import { JobTaskGroup } from './types';
import { UNSCHEDULED_KEY } from './constants';

interface GroupDraft {
  position: number;
  dated: Map<string, JobTask[]>;
  unscheduled: JobTask[];
}

export const groupStageTasks = (data: JobTaskData[]): JobTaskGroup[] => {
  const drafts = new Map<string, GroupDraft>();

  data.forEach((entry) => {
    entry.tasks.forEach((task) => {
      const groupable = Boolean(task.schedule) || isSchedulable(task);
      const key = (groupable && task.unitIdentityId) || task.id;
      const draft = drafts.get(key) || {
        position: task.position ?? 0,
        dated: new Map<string, JobTask[]>(),
        unscheduled: [],
      };
      draft.position = Math.min(draft.position, task.position ?? 0);
      if (isWithoutSchedule(entry)) draft.unscheduled.push(task);
      else draft.dated.set(entry.date, [...(draft.dated.get(entry.date) || []), task]);
      drafts.set(key, draft);
    });
  });

  return [...drafts.entries()]
    .map(([key, draft]) => {
      const days = parseJobDays(
        [...draft.dated.entries()].map(([date, tasks]) => ({ date, tasks })),
      );
      const scheduledCount = days.reduce((sum, day) => sum + day.tasks.length, 0);
      return {
        key,
        position: draft.position,
        days,
        unscheduled: sortByProp<JobTask>(draft.unscheduled, 'position', 'asc'),
        total: scheduledCount + draft.unscheduled.length,
        needsSchedule:
          !days.length &&
          draft.unscheduled.some((t) => isSchedulable(t) && getAction(t) === 'schedule'),
      };
    })
    .sort((a, b) => a.position - b.position);
};

export const getDefaultGroupKey = (days: JobDay[]) => {
  if (!days.length) return UNSCHEDULED_KEY;
  const today = days.find((day) => isToday(day.day));
  if (today) return today.key;
  return (
    (days.find((day) => !isBefore(day.day, startOfToday())) || days.at(-1))?.key || UNSCHEDULED_KEY
  );
};

export const getGroupCompletion = (group: JobTaskGroup): boolean | 'indeterminate' => {
  const tasks = [...group.days.flatMap((day) => day.tasks), ...group.unscheduled];
  const done = tasks.filter((task) => task.status === 'completed').length;
  if (done && done === tasks.length) return true;
  return done ? 'indeterminate' : false;
};
